import { FieldValues, Path, UseFormReturn } from "react-hook-form"

import toast from "@molecules/Toast/Toast"

import getApiErrorMessages from "./getApiErrorMessages"

/**
 * Applies a DRF-style error response onto a react-hook-form form.
 *
 * Backend keys present in `formFieldMap` are surfaced inline via `form.setError`. Any unmapped
 * key (including `NON_FIELD_ERRORS`) is routed to `setNonFieldError` so the caller can render
 * it. The last unmapped message wins. When the response contains nothing recognizable, an error toast is
 * fired with `genericMessage`.
 *
 * If a key returns multiple messages, only the first is shown.
 */
export default function applyApiErrorsToForm<TFormValues extends FieldValues>(
    form: UseFormReturn<TFormValues>,
    error: ApiErrorResponse,
    formFieldMap: Partial<Record<string, Path<TFormValues>>>,
    genericMessage: string,
    setNonFieldError: (message: string) => void,
): void {
    let hasHandledError = false

    for (const [backendKey, value] of Object.entries(error)) {
        const messages = getApiErrorMessages(value)
        if (messages.length === 0) {
            continue
        }

        const formField = formFieldMap[backendKey]
        if (formField) {
            form.setError(formField, { type: "server", message: messages[0] })
        } else {
            setNonFieldError(messages[0])
        }
        hasHandledError = true
    }

    if (!hasHandledError) {
        toast({ title: genericMessage, type: "error" })
    }
}
